import { Button } from '@/components/ui/Button';
import { RotateCcw, X } from 'lucide-react';
import { useFolderDetail } from '@/hooks/useDomains';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Messages } from '../components/Messages';
import { Saisie } from '../components/Saisie';
import { Suggestions } from '../components/Suggestions';
import { useAssistant } from '../hooks/useAssistant';
import { useAssistantDisponible } from '../hooks/useAssistantDisponible';
import type { ContexteDossier } from '../types/assistant.types';

/**
 * L'assistant — un écran plein, une seule tâche : dire ce qu'on veut.
 *
 * Pas de bandeau, pas de carte d'indicateurs : le contenu commence haut.
 * Ouvert depuis un dossier, il le garde en contexte (pastille retirable)
 * pour ne pas avoir à le répéter à chaque demande.
 */
export function AssistantPage() {
  const [params, setParams] = useSearchParams();
  // Le nom du dossier vient du serveur, jamais de l'URL : une adresse forgée
  // ne fait pas dire à l'écran un nom qui n'est pas le sien (sécurité, 12/09).
  // Le dossier vient de l'adresse À CHAQUE navigation, pas seulement à
  // l'ouverture : « Assistant » depuis un autre dossier change le contexte, et
  // la conversation repart à zéro (verdict QA n°4 : sinon la demande partait
  // sur le client précédent).
  const dossierUrl = params.get('dossier');
  const [dossierUuid, setDossierUuid] = useState<string | null>(dossierUrl);
  const {
    data: dossier,
    isPending: dossierEnAttente,
    isError: dossierEnErreur,
  } = useFolderDetail(dossierUuid ?? undefined);
  // Arrivé par une adresse collée, on n'envoie rien tant que le dossier du
  // contexte n'est pas connu : une demande partirait sans lui, en silence.
  const contexteEnAttente =
    !!dossierUuid && dossierEnAttente && !dossierEnErreur;
  const contexte: ContexteDossier | null =
    dossierUuid && dossier?.data
      ? { uuid: dossierUuid, nom: dossier.data.name }
      : null;
  const [suggestion, setSuggestion] = useState<string | undefined>();
  const [cleSuggestion, setCleSuggestion] = useState(0);

  const {
    messages,
    enAttente,
    actionEnSuspens,
    occupe,
    envoyer,
    confirmer,
    annuler,
    recommencer,
  } = useAssistant(contexte);

  // Sans clé sur le serveur, l'écran le dit tout de suite au lieu de laisser
  // taper une demande qui échouera — et sans réponse, il ne suppose rien.
  const { disponible, ecriture, raison, chargement } = useAssistantDisponible();

  // Pourquoi on ne peut pas changer de dossier maintenant, quand c'est le cas.
  const [avertissement, setAvertissement] = useState<string | null>(null);
  const MOTIF_OCCUPE =
    'Une action attend votre décision ou est en cours : terminez-la avant de changer de dossier.';

  useEffect(() => {
    if (dossierUrl === dossierUuid) return;
    if (occupe) {
      // Le lien « Assistant » de l'en-tête, ou une adresse tapée, ne doit
      // pas faire disparaître une action à confirmer ni une action que l'on
      // suit : on remet l'adresse, on le dit, et on ne touche pas au fil.
      if (dossierUuid) params.set('dossier', dossierUuid);
      else params.delete('dossier');
      setParams(params, { replace: true });
      setAvertissement(MOTIF_OCCUPE);
      return;
    }
    setAvertissement(null);
    setDossierUuid(dossierUrl);
    // Seul un dossier réellement transmis a orienté la conversation : un
    // dossier inaccessible n'a jamais été envoyé, le fil reste.
    if (messages.length > 0 && contexte) recommencer();
    // `messages`, `contexte`, `occupe`, `params` volontairement hors
    // dépendances : seul le changement d'adresse déclenche ce recalage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierUrl]);

  // La phrase ne survit pas à ce qui la justifiait : action terminée, elle s'efface.
  useEffect(() => {
    if (!occupe) setAvertissement(null);
  }, [occupe]);

  const finRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    finRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [messages, enAttente]);

  // Le serveur ne rejoue au modèle que les tours du dossier ouvert (les
  // paramètres d'un autre client ne repartent pas avec la demande) : quand le
  // dossier change, la conversation repart à zéro, et l'écran le montre.
  const retirerContexte = () => {
    // Retirer un dossier réellement en contexte repart sur une conversation
    // neuve (le serveur ne rejoue que les tours de ce dossier) ; retirer une
    // pastille « inaccessible » ne change rien au fil : ce dossier n'a jamais
    // été transmis.
    if (occupe) {
      setAvertissement(MOTIF_OCCUPE);
      return;
    }
    setAvertissement(null);
    const avaitContexte = !!contexte;
    setDossierUuid(null);
    params.delete('dossier');
    setParams(params, { replace: true });
    if (avaitContexte) recommencer();
  };

  const vide = messages.length === 0;

  return (
    <div
      className={`mx-auto flex max-w-3xl flex-col ${
        vide ? '' : 'min-h-[calc(100vh-13rem)]'
      }`}
    >
      {/* Une ligne : le nom de l'écran, le contexte, et recommencer. */}
      <div className="flex min-h-10 items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="text-xl font-bold text-neutral-900">Assistant</h1>
          {/* Dossier demandé par l'adresse mais inaccessible : on le dit, la
              demande partira sans lui (et il se retire d'un clic). */}
          {dossierUuid && dossierEnErreur && !contexte && (
            <span className="bg-warning-50 text-warning-700 flex min-h-7 items-center gap-1 rounded-full pr-1 pl-3 text-xs font-medium">
              Dossier inaccessible
              <button
                type="button"
                onClick={retirerContexte}
                aria-label="Retirer ce dossier"
                className="hover:bg-warning-100 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          {contexte && (
            <span className="bg-primary-50 text-primary-700 flex min-h-7 items-center gap-1 rounded-full pr-1 pl-3 text-xs font-medium">
              {contexte.nom}
              <button
                type="button"
                onClick={retirerContexte}
                aria-disabled={occupe}
                title={occupe ? MOTIF_OCCUPE : undefined}
                aria-label={`Ne plus parler de ${contexte.nom}`}
                className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                  occupe
                    ? 'cursor-not-allowed opacity-40'
                    : 'hover:bg-primary-100'
                }`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>
        {!vide && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (occupe) {
                setAvertissement(MOTIF_OCCUPE);
                return;
              }
              setAvertissement(null);
              recommencer();
            }}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Nouvelle conversation
          </Button>
        )}
      </div>
      {avertissement && (
        <p role="status" className="text-warning-700 mt-1 text-xs">
          {avertissement}
        </p>
      )}

      {/* Vide : la saisie suit les exemples, sans trou. En conversation : la
          saisie reste en bas, le fil prend l'espace. */}
      <div className={vide ? 'py-6' : 'flex-1 py-6'}>
        {/* Pas de mode d'emploi à l'accueil (Rose, 11/09/2026) : les exemples
            suffisent, l'assistant dit lui-même ce qu'il sait faire, et la
            documentation aura sa page. */}
        {vide ? (
          <Suggestions
            onChoisir={texte => {
              setSuggestion(texte);
              setCleSuggestion(n => n + 1);
            }}
            ecriture={ecriture}
          />
        ) : (
          <Messages
            messages={messages}
            enAttente={enAttente}
            onConfirmer={confirmer}
            onAnnuler={annuler}
          />
        )}
        {/* Marge de défilement = hauteur de la saisie collée en bas, pour que
            la dernière réponse ne finisse jamais cachée dessous. */}
        <div ref={finRef} className="scroll-mb-28" />
      </div>

      <div className="sticky bottom-0 bg-gray-50 pt-2 pb-2">
        <Saisie
          onEnvoyer={texte => {
            setSuggestion(undefined);
            void envoyer(texte);
          }}
          bloque={
            enAttente ||
            actionEnSuspens ||
            chargement ||
            contexteEnAttente ||
            disponible !== true
          }
          raisonBlocage={
            raison ??
            (actionEnSuspens
              ? 'Confirmez ou annulez l’action ci-dessus avant de continuer.'
              : contexteEnAttente
                ? 'Un instant, je retrouve le dossier…'
                : undefined)
          }
          texteInitial={suggestion}
          cleSuggestion={cleSuggestion}
        />
      </div>
    </div>
  );
}
